import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { useVisibleApplicationScopes } from '@/hooks/useApplicationScopes';
import { useVisibleScopesSubscription } from '@/hooks/useVisibleScopesSubscription';
import { useLens } from '@/hooks/useLens';
import { useRolePreviewStore } from '@/stores/rolePreviewStore';
import type { ApplicationScope } from '@/lib/graphql/applicationscope';
import type { ResolvedLens } from '@/lib/graphql/lens';

const STORAGE_KEY = 'ix:activeScope';

interface ApplicationScopeContextValue {
  activeScope: ApplicationScope | null;
  activateScope: (scopeId: string) => void;
  visibleScopes: ApplicationScope[];
  loading: boolean;
  resolvedLens: ResolvedLens | null;
  /**
   * The visibleApplicationScopes query failed. Consumers MUST distinguish this from
   * an empty visibleScopes array: without it, a backend outage is indistinguishable
   * from a principal who legitimately has zero scopes, and the shell renders a
   * confident empty nav over a 502 (CDEV-1456).
   */
  scopesError: Error | null;
  /**
   * The lens query failed. This degrades rather than blocks: scope ordering falls back
   * to orderHint and the landing route falls back to '/', so the nav still renders.
   * Exposed for the same reason as scopesError — so the failure is observable rather
   * than swallowed into a null resolvedLens that looks like "no lens configured".
   */
  lensError: Error | null;
  /** True while a retry() refetch is in flight. */
  retrying: boolean;
  /**
   * Refetch scopes and lens. Uses the query instances' own refetch, so it always
   * targets the active keys — ['applicationScopes', previewRoleId] and ['myLens'] —
   * rather than the un-previewed entry (CDEV-1454).
   */
  retry: () => void;
}

const ApplicationScopeContext = createContext<ApplicationScopeContextValue>({
  activeScope: null,
  activateScope: () => {},
  visibleScopes: [],
  loading: true,
  resolvedLens: null,
  scopesError: null,
  lensError: null,
  retrying: false,
  retry: () => {},
});

export function useApplicationScopeContext() {
  return useContext(ApplicationScopeContext);
}

export function ApplicationScopeProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  // Don't fire the scope query until we have a non-expired token.
  // Without this guard, queries fire with a stale/missing token → 401 flood.
  // Under a role preview (CDEV-1454) the nav is derived from the previewed role's
  // capability bundle, so sections the role can't reach disappear rather than
  // rendering the real principal's full sidebar.
  const previewRoleId = useRolePreviewStore((s) => s.previewRoleId);
  const {
    data: scopes,
    isLoading,
    isError,
    error: scopesQueryError,
    isFetching: scopesFetching,
    refetch: refetchScopes,
  } = useVisibleApplicationScopes({
    enabled: auth.isAuthenticated,
    previewRoleId,
  });
  const {
    data: lensData,
    isLoading: lensLoading,
    isError: lensIsError,
    error: lensQueryError,
    isFetching: lensFetching,
    refetch: refetchLens,
  } = useLens({
    enabled: auth.isAuthenticated,
  });
  const [activeScopeId, setActiveScopeId] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY),
  );

  useVisibleScopesSubscription({ enabled: auth.isAuthenticated });

  const visibleScopes = scopes ?? [];

  // Memoized so the identity is stable while the query stays in its error state —
  // these are effect dependencies and context value fields.
  const scopesError = useMemo(
    () => (isError ? ((scopesQueryError as Error | null) ?? new Error('Failed to load application scopes')) : null),
    [isError, scopesQueryError],
  );
  const lensError = useMemo(
    () => (lensIsError ? ((lensQueryError as Error | null) ?? new Error('Failed to resolve lens')) : null),
    [lensIsError, lensQueryError],
  );
  const retrying = (scopesError !== null || lensError !== null) && (scopesFetching || lensFetching);

  // A lens failure is survivable — say so once rather than leaving a null resolvedLens
  // to read as "no lens configured" in the console as well as the UI.
  useEffect(() => {
    if (lensError) {
      console.warn('[ApplicationScopeContext] Lens resolution failed — falling back to orderHint ordering and the default landing route:', lensError.message);
    }
  }, [lensError]);

  const retry = useCallback(() => {
    void refetchScopes();
    void refetchLens();
  }, [refetchScopes, refetchLens]);

  // Auto-select scope if stored scope is no longer visible or if only one scope exists.
  // Prefer the lens defaultScopeId when available.
  useEffect(() => {
    if (isLoading || lensLoading || visibleScopes.length === 0) return;

    const storedIsValid = activeScopeId && visibleScopes.some((s) => s.scopeId === activeScopeId);
    if (!storedIsValid) {
      const lensDefault = lensData?.defaultScopeId;
      const lensIsValid = lensDefault && visibleScopes.some((s) => s.scopeId === lensDefault);
      const selected = lensIsValid ? lensDefault : visibleScopes[0].scopeId;
      setActiveScopeId(selected);
      // Don't persist a scope chosen under a role preview — the previewed role's
      // narrower scope set would otherwise overwrite the operator's real active
      // scope and survive after they exit the preview (CDEV-1454).
      if (!previewRoleId) {
        localStorage.setItem(STORAGE_KEY, selected);
      }
    }
  }, [isLoading, lensLoading, visibleScopes, activeScopeId, lensData, previewRoleId]);

  // Leaving a preview restores the operator's own active scope from storage —
  // otherwise they stay parked on whatever scope the previewed role fell back to.
  useEffect(() => {
    if (previewRoleId) return;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored !== activeScopeId) {
      setActiveScopeId(stored);
    }
    // Intentionally keyed on previewRoleId only: this runs on preview exit, not
    // on every activeScopeId change (which would fight activateScope).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewRoleId]);

  const activateScope = (scopeId: string) => {
    setActiveScopeId(scopeId);
    localStorage.setItem(STORAGE_KEY, scopeId);
  };

  const activeScope = visibleScopes.find((s) => s.scopeId === activeScopeId) ?? null;

  return (
    <ApplicationScopeContext.Provider
      value={{
        activeScope,
        activateScope,
        visibleScopes,
        // An errored query is never "still loading" — but it is also not a resolved
        // empty state. Consumers branch on scopesError before visibleScopes.length.
        loading: (isLoading || lensLoading) && !isError && !lensIsError,
        resolvedLens: lensData ?? null,
        scopesError,
        lensError,
        retrying,
        retry,
      }}
    >
      {children}
    </ApplicationScopeContext.Provider>
  );
}
